// Shared between ItemsMeasureUnits (container-owned drafts, Part A) and
// ItemCard (controlled presentation + immediate duplicate). Item-field edits
// are draft-until-Save now: the container keeps one draft per item id (plus
// a list of local-only "pending new item" drafts) and ItemCard never talks
// to the server for its own field edits anymore.
const LB_TO_KG = 0.453592;

// Numeric-input guards (shared by ItemCard / UnitRow / SummaryBar): measurement
// and quantity fields must reject alphabets and symbols as the user types.
// `toDecimalInput` keeps digits and a single decimal point (weight, L/W/H,
// declared value); `toIntegerInput` keeps digits only (Pieces, HUs). Both are
// applied in the input's onChange so nothing invalid ever reaches the draft.
export function toDecimalInput(value) {
  let s = String(value == null ? "" : value).replace(/[^0-9.]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
  }
  return s;
}

export function toIntegerInput(value) {
  return String(value == null ? "" : value).replace(/[^0-9]/g, "");
}

// OrderItemUnit#convert_to_lb treats only 'kg'/'kgs' as kilograms; everything
// else (including blank) is already pounds. Mirrored here for display only.
function toLb(value, uom) {
  const num = parseFloat(value) || 0;
  return ["kg", "kgs"].includes(String(uom || "").toLowerCase())
    ? num / LB_TO_KG
    : num;
}

// Rounded display value for the weight input, derived from the exact line
// total and the current entry mode. Display-only: it is never fed back into
// the persisted total unless the user actually types a new number.
function weightDisplayValue(totalExact, entryType, handlingUnits) {
  const exact = parseFloat(totalExact);
  if (!Number.isFinite(exact)) return "0.00";
  if (entryType === "per_hu") {
    const hus = parseInt(handlingUnits, 10) || 1;
    return (exact / hus).toFixed(2);
  }
  return exact.toFixed(2);
}

// The item form is a full-object save (update_customer_order_item /
// create_customer_order_item read top-level params directly, not a sparse
// PATCH like the order_item_units endpoints), so the draft always mirrors
// every editable field.
//
// Weight is double-tracked: `weight_total_exact` is the source of truth (the
// exact line total in the selected uom, never rounded), and `weight_input`
// is the rounded display string derived from it per the current entry mode.
// A mode toggle re-derives the display but leaves the exact total untouched,
// so toggling can never drift the persisted item_weight (100 / 3 HUs shows
// 33.33 per HU but still persists exactly 100). Only an actual numeric edit
// (weight input, or HUs while in per-HU mode) replaces the exact total.
//
// buildDraft({}) (no source item) is the pristine "empty mockup item"
// baseline used both to seed a brand-new "Add N Items" draft card and to
// detect a fully-untouched draft that should be silently dropped on Save
// (see isEmptyDraft below).
export function buildDraft(item) {
  const source = item || {};
  // HUs are independent of pieces (user decision 2026-08-05): use the stored
  // handling_units as-is; a new/legacy item with none defaults to 1 (one
  // scannable unit), NOT to the piece count.
  const handlingUnits =
    source.handling_units !== null && source.handling_units !== undefined
      ? source.handling_units
      : 1;
  // Mapping wave (user decision, legacy-consistent): the Item Total / By
  // Handling Unit toggle maps directly to legacy is_indv_weight (0 -> Item
  // Total, 1 -> By Handling Unit) — `weight_entry_type` below is this
  // component tree's own internal UI vocabulary for the toggle (never sent
  // to the server as such; see buildItemPayload/buildNewItemPayload, which
  // emit `is_indv_weight` instead), derived here from the persisted
  // is_indv_weight rather than from the OrderItem field of the same old
  // name (now unused). A brand-new draft (source === {}, is_indv_weight
  // undefined) defaults to "total", matching the pre-mapping-wave default.
  const weightEntryType = Number(source.is_indv_weight) === 1 ? "per_hu" : "total";
  const totalWeightLb = toLb(source.item_weight, source.item_weight_uom);
  const weightUom = ["kg", "kgs"].includes(String(source.item_weight_uom || "").toLowerCase())
    ? "kg"
    : "lb";
  const totalInSelectedUom = weightUom === "kg" ? totalWeightLb * LB_TO_KG : totalWeightLb;
  const totalExact = Number.isFinite(totalInSelectedUom) ? totalInSelectedUom : 0;

  return {
    // M3 (user decision): the "Item Description" field reads/writes
    // item_name — the card header's displayed name — not the separate
    // item_description column, which this screen no longer touches at all.
    item_name: source.item_name || "",
    item_model: source.item_model || "",
    serial_number: source.serial_number || "",
    // M1 (user decision, full replace): item-level "Storage Location" maps
    // to legacy wh_dock, not the new (now-unused) storage_location field.
    // Unit-level storage_location is untouched — see UnitRow's own draft.
    wh_dock: source.wh_dock || "",
    item_quantity:
      source.item_quantity !== null && source.item_quantity !== undefined ? source.item_quantity : 1,
    // Placeholder, no default (user decision): a new item starts with no piece
    // type selected (the dropdown shows "Select Piece Type"); a persisted item
    // keeps whatever it has. "Pieces"/"Handling Units" are no longer defaults.
    piece_type: source.piece_type || "",
    handling_units: handlingUnits,
    // Wave A / Fix 1: mirrors OrderItem#hus_manual (order_item.rb:87) so the
    // draft itself carries the same "has the user (this record) ever
    // deliberately set HUs independent of Pieces" flag the server's
    // before_save hook (default_handling_units, order_item.rb:101-104)
    // gates on. Seeded from the persisted flag; applyFieldChange below flips
    // it true the instant the user types directly into the HUs box, and
    // gates the Pieces->HUs auto-follow while it's false.
    hus_manual: !!source.hus_manual,
    handling_unit_type: source.handling_unit_type || "",
    weight_entry_type: weightEntryType,
    weight_total_exact: totalExact,
    weight_input: weightDisplayValue(totalExact, weightEntryType, handlingUnits),
    item_weight_uom: weightUom,
    item_length: source.item_length !== null && source.item_length !== undefined ? source.item_length : "",
    item_width: source.item_width !== null && source.item_width !== undefined ? source.item_width : "",
    item_height: source.item_height !== null && source.item_height !== undefined ? source.item_height : "",
    item_length_uom: source.item_length_uom || "in",
    // Per-item Dim factor: seed from the persisted item so an existing selection
    // shows; blank ("") when none is set yet (dropdown shows the placeholder and
    // DIM weight falls back to the account factor until one is chosen).
    dim_factor: source.dim_factor !== null && source.dim_factor !== undefined ? source.dim_factor : "",
  };
}

// The persisted item_weight always comes from the exact total, never from
// the rounded display string.
export function computeTotalWeight(draft) {
  const exact = parseFloat(draft.weight_total_exact);
  if (!Number.isFinite(exact)) return 0;
  return parseFloat(exact.toFixed(4));
}

// Fields compared numerically for dirty-checking, so "40" vs "40.00" (or an
// unchanged re-typed value) doesn't count as a change.
const NUMERIC_DRAFT_FIELDS = [
  "item_quantity",
  "handling_units",
  "weight_total_exact",
  "item_length",
  "item_width",
  "item_height",
];

export function draftEquals(a, b) {
  if (!a || !b) return a === b;
  return Object.keys(a).every((key) => {
    // Display-only mirror of weight_total_exact; a mode toggle changes it
    // (100.00 -> 33.33) without any real data change, so it must not count.
    if (key === "weight_input") return true;
    if (NUMERIC_DRAFT_FIELDS.includes(key)) {
      const numA = parseFloat(a[key]);
      const numB = parseFloat(b[key]);
      const aNaN = Number.isNaN(numA);
      const bNaN = Number.isNaN(numB);
      if (aNaN && bNaN) return true; // both blank/unparseable
      return !aNaN && !bNaN && numA === numB;
    }
    return String(a[key] == null ? "" : a[key]) === String(b[key] == null ? "" : b[key]);
  });
}

// A freshly-added "Add N Items" draft card the user never touched (still
// exactly the pristine default). Save silently drops these instead of
// calling createItem with an all-defaults item (Part A2/A3).
export function isEmptyDraft(draft) {
  return draftEquals(draft, buildDraft({}));
}

// Text-field edit: updates the draft only (no server call — Part A1 removes
// the old debounced auto-persist entirely). An actual numeric edit is the
// ONLY thing that replaces the exact line total: typing in the weight input
// (converted per the current mode), or changing HUs while in per-HU mode
// (displayed per-HU value x new HUs).
export function applyFieldChange(draft, field, value) {
  const next = { ...draft, [field]: value };
  if (field === "weight_input") {
    const entered = parseFloat(value);
    const hus = parseInt(draft.handling_units, 10) || 1;
    next.weight_total_exact = Number.isNaN(entered)
      ? 0
      : draft.weight_entry_type === "per_hu"
      ? entered * hus
      : entered;
  } else if (field === "handling_units" && draft.weight_entry_type === "per_hu") {
    const perHu = parseFloat(draft.weight_input);
    const hus = parseInt(value, 10) || 1;
    if (!Number.isNaN(perHu)) next.weight_total_exact = perHu * hus;
  }

  if (field === "handling_units") {
    // A deliberate HU edit pins the value: from here on, typing Pieces no
    // longer mirrors into HUs (buildItemPayload keys hus_manual off
    // touched.handling_units too).
    next.hus_manual = true;
  }

  // Pieces -> Handling Units default (user decision 2026-08-11, reversing the
  // 2026-08-05 independence rule): entering Pieces sets Handling Units to the
  // same value BY DEFAULT, until the user changes HUs themselves (hus_manual).
  // The scannable-unit count follows HUs, so this makes "5 pieces" start as
  // "5 handling units" while still letting them override. Skipped on a blank
  // entry so clearing Pieces mid-retype doesn't wipe HUs, and it leaves
  // hus_manual false so it stays an auto-follow (not a manual pin).
  if (
    field === "item_quantity" &&
    !draft.hus_manual &&
    value !== "" &&
    value !== null &&
    value !== undefined
  ) {
    next.handling_units = value;
    if (next.weight_entry_type === "per_hu") {
      const perHu = parseFloat(next.weight_input);
      const hus = parseInt(value, 10) || 1;
      if (!Number.isNaN(perHu)) next.weight_total_exact = perHu * hus;
    }
  }
  return next;
}

// Select-field edit. Toggling the weight entry mode only re-derives the
// rounded DISPLAY value from the exact total (100 / 3 HUs -> shows 33.33) —
// weight_total_exact, and therefore the persisted item_weight, is untouched,
// so a mode-only toggle can never drift the stored total.
export function applySelectChange(draft, field, value) {
  if (draft[field] === value) return draft;
  const next = { ...draft, [field]: value };
  if (field === "weight_entry_type") {
    next.weight_input = weightDisplayValue(draft.weight_total_exact, value, draft.handling_units);
  }
  return next;
}

// True when the draft's handling_units field was left blank by the user —
// B-M7: on a PERSISTED item this must mean "unchanged", never
// handling_units: 0 (buildItemPayload below omits the key entirely so the
// backend's presence-guarded assignment leaves the stored value alone).
function handlingUnitsBlank(draft) {
  return draft.handling_units === "" || draft.handling_units === null || draft.handling_units === undefined;
}

// Payload for update_customer_order_item (an existing, persisted item).
export function buildItemPayload({ item, draft, touched, orderId, orgId, accountId }) {
  return {
    item_id: item.id,
    customer_order_id: orderId,
    org_id: orgId,
    account_id: accountId,
    // M3 (user decision): the "Item Description" field reads/writes
    // item_name (the card header's name), not the separate item_description
    // column — this screen no longer sends item_description at all. Always
    // sent (even "") so a deliberately-blanked field actually persists
    // blank, matching update_order_item's `present? || == ''` guard.
    item_name: draft.item_name,
    item_model: draft.item_model,
    serial_number: draft.serial_number,
    // M1 (user decision, full replace): item-level Storage Location maps to
    // legacy wh_dock.
    wh_dock: draft.wh_dock,
    item_quantity: parseInt(draft.item_quantity, 10) || 1,
    piece_type: draft.piece_type,
    // B-M7: blank HUs input means "leave unchanged" — omit the key (dropped
    // from the JSON body) rather than coercing to 0, so the backend's
    // `if params[:handling_units].present?` guard skips the assignment.
    handling_units: handlingUnitsBlank(draft) ? undefined : parseInt(draft.handling_units, 10) || 0,
    handling_unit_type: draft.handling_unit_type,
    // Manual whenever the user has actually typed into the HUs box this
    // session; otherwise preserve whatever the server already had.
    hus_manual: touched.handling_units ? true : !!item.hus_manual,
    // M2 (legacy-consistent mapping): the Item Total / By Handling Unit
    // toggle maps to legacy is_indv_weight (0/1), sent explicitly —
    // update_order_item only honors this key (and the piece_type/
    // handling_units/handling_unit_type/hus_manual/kg-mirror/dims-fan-out
    // block above/below it) when from_units_screen marks this as coming
    // from this screen, never off is_indv_weight's own presence (legacy
    // ItemForm.jsx round-trips a whole item object that already carries its
    // own is_indv_weight).
    is_indv_weight: draft.weight_entry_type === "per_hu" ? 1 : 0,
    from_units_screen: "true",
    item_weight_uom: draft.item_weight_uom,
    // item_weight is always the OrderItem line total (see
    // OrderItemUnitSyncService.item_total_weight_lb); when the entry mode is
    // "By Handling Unit" the total is derived here before sending.
    // weight_type: 'complete' stops the legacy update_order_item path from
    // re-multiplying by item_quantity (is_indv_weight above carries the
    // actual toggle instead).
    item_weight: computeTotalWeight(draft),
    weight_type: "complete",
    item_length: parseFloat(draft.item_length) || 0,
    item_width: parseFloat(draft.item_width) || 0,
    item_height: parseFloat(draft.item_height) || 0,
    // Single param the update path fans out to all three item uom fields,
    // same convention the create path (OrderItemCreateService) uses.
    dimensions_units: draft.item_length_uom,
    item_length_uom: draft.item_length_uom,
    // Per-item selected Dim factor. Sent from the DRAFT so a dropdown change
    // persists; a blank selection sends "" and the backend
    // (resolve_account_dim_factor) falls back to the account's factor.
    dim_factor: draft.dim_factor,
  };
}

// Payload for create_customer_order_item (a brand-new "Add N Items" draft
// card with no server-side item yet — no item_id/dim_factor to carry over).
export function buildNewItemPayload({ draft, touched, orderId, orgId, accountId }) {
  return {
    customer_order_id: orderId,
    org_id: orgId,
    account_id: accountId,
    item_name: draft.item_name,
    item_model: draft.item_model,
    serial_number: draft.serial_number,
    wh_dock: draft.wh_dock,
    item_quantity: parseInt(draft.item_quantity, 10) || 1,
    piece_type: draft.piece_type,
    handling_units: handlingUnitsBlank(draft) ? undefined : parseInt(draft.handling_units, 10) || 0,
    handling_unit_type: draft.handling_unit_type,
    hus_manual: !!(touched && touched.handling_units),
    is_indv_weight: draft.weight_entry_type === "per_hu" ? 1 : 0,
    from_units_screen: "true",
    item_weight_uom: draft.item_weight_uom,
    item_weight: computeTotalWeight(draft),
    weight_type: "complete",
    item_length: parseFloat(draft.item_length) || 0,
    item_width: parseFloat(draft.item_width) || 0,
    item_height: parseFloat(draft.item_height) || 0,
    dimensions_units: draft.item_length_uom,
    item_length_uom: draft.item_length_uom,
    // Carry the picked per-item Dim factor onto a brand-new item too (blank =
    // account fallback), so a Dim chosen before the first Save isn't lost.
    dim_factor: draft.dim_factor,
  };
}
